import React, { useState, useRef, useEffect } from 'react';
import {
  X,
  UploadCloud,
  FileText,
  Sparkles,
  CheckCircle2,
  Calendar,
  Tag as TagIcon,
  Folder,
  Shield,
  Lock,
  Eye,
  AlertCircle,
  FileSpreadsheet,
  FileImage,
  FileArchive,
  FileCode,
  FileCheck,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

export const PREDEFINED_DOC_TYPES = [
  { id: 'pdf', label: 'PDF Document', icon: FileText, color: '#ef4444' },
  { id: 'identity_document', label: 'Identity Document', icon: Shield, color: '#8b5cf6' },
  { id: 'certificate', label: 'Certificate', icon: FileCheck, color: '#10b981' },
  { id: 'contract', label: 'Contract / Agreement', icon: FileText, color: '#3b82f6' },
  { id: 'receipt', label: 'Receipt / Invoice', icon: FileSpreadsheet, color: '#f59e0b' },
  { id: 'financial_document', label: 'Financial Document', icon: FileSpreadsheet, color: '#059669' },
  { id: 'property_document', label: 'Property Document', icon: Folder, color: '#d97706' },
  { id: 'work_document', label: 'Work Document', icon: FileText, color: '#6366f1' },
  { id: 'insurance_document', label: 'Insurance Document', icon: Shield, color: '#0284c7' },
  { id: 'vehicle_document', label: 'Vehicle Document', icon: FileText, color: '#ea580c' },
  { id: 'legal_document', label: 'Legal Document', icon: FileText, color: '#7c3aed' },
  { id: 'other', label: 'Other / Custom', icon: FileText, color: '#64748b' },
];

export const PREDEFINED_CATEGORIES = [
  'Personal',
  'Work',
  'Financial',
  'Legal',
  'Property',
  'Insurance',
  'Vehicle',
  'Health',
  'Education',
  'Other',
];

export function getFileFormatInfo(filename = '') {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  switch (ext) {
    case 'pdf':
      return { format: 'PDF', icon: FileText, color: '#ef4444', badgeClass: 'format-pdf' };
    case 'doc':
    case 'docx':
      return { format: 'DOCX', icon: FileText, color: '#2563eb', badgeClass: 'format-doc' };
    case 'xls':
    case 'xlsx':
    case 'csv':
      return { format: 'XLSX', icon: FileSpreadsheet, color: '#16a34a', badgeClass: 'format-xls' };
    case 'ppt':
    case 'pptx':
      return { format: 'PPTX', icon: FileText, color: '#ea580c', badgeClass: 'format-ppt' };
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'webp':
    case 'svg':
      return { format: ext.toUpperCase(), icon: FileImage, color: '#9333ea', badgeClass: 'format-img' };
    case 'txt':
    case 'md':
      return { format: 'TXT', icon: FileCode, color: '#64748b', badgeClass: 'format-txt' };
    case 'zip':
    case 'rar':
    case 'tar':
    case 'gz':
      return { format: 'ZIP', icon: FileArchive, color: '#d97706', badgeClass: 'format-zip' };
    default:
      return { format: ext.toUpperCase() || 'FILE', icon: FileText, color: '#64748b', badgeClass: 'format-misc' };
  }
}

export function formatBytes(bytes, decimals = 1) {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

export default function DocumentUploadModal({
  open,
  onClose,
  onSave,
  categories = PREDEFINED_CATEGORIES,
  existingDocument = null,
}) {
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [fileDataUrl, setFileDataUrl] = useState(existingDocument?.fileDataUrl || '');
  const [fileSize, setFileSize] = useState(existingDocument?.fileSize || 0);
  const [fileName, setFileName] = useState(existingDocument?.fileName || '');
  const [fileType, setFileType] = useState(existingDocument?.fileType || 'application/pdf');

  // Form Fields
  const [documentName, setDocumentName] = useState(existingDocument?.title || '');
  const [documentType, setDocumentType] = useState(existingDocument?.documentType || 'pdf');
  const [category, setCategory] = useState(existingDocument?.category || 'Personal');
  const [description, setDescription] = useState(existingDocument?.description || '');
  const [issueDate, setIssueDate] = useState(existingDocument?.issueDate || '');
  const [expiryDate, setExpiryDate] = useState(existingDocument?.expiryDate || '');
  const [owner, setOwner] = useState(existingDocument?.owner || user?.name || user?.email || 'You');
  const [tags, setTags] = useState(existingDocument?.tags || []);
  const [tagInput, setTagInput] = useState('');

  // Security Lock
  const [isLocked, setIsLocked] = useState(existingDocument?.isLocked || false);
  const [lockPin, setLockPin] = useState(existingDocument?.lockPin || '');

  // Auto-Detect state
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [autoDetectInsights, setAutoDetectInsights] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // Lock body scroll on mobile
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (open) window.document.body.classList.add('mobile-modal-open');
    else window.document.body.classList.remove('mobile-modal-open');
    return () => {
      if (typeof window !== 'undefined') {
        window.document.body.classList.remove('mobile-modal-open');
      }
    };
  }, [open]);

  if (!open) return null;

  // Auto Detect & Categorize Engine
  const analyzeFileAndSuggestMetadata = (selectedFile) => {
    setIsAnalyzing(true);
    const fname = selectedFile.name;
    const lower = fname.toLowerCase();
    const cleanName = fname.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
    const formattedTitle = cleanName.charAt(0).toUpperCase() + cleanName.slice(1);

    setTimeout(() => {
      let suggestedType = 'other';
      let suggestedCategory = 'Personal';
      let suggestedTags = [];
      let detectedExpiry = '';
      let detectedIssue = '';
      let detectedDetails = [];

      const today = new Date();

      if (lower.includes('lease') || lower.includes('rent') || lower.includes('tenant')) {
        suggestedType = 'property_document';
        suggestedCategory = 'Property';
        suggestedTags = ['lease', 'property', 'real-estate'];
        detectedExpiry = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate()).toISOString().split('T')[0];
        detectedDetails.push('Detected residential/commercial lease agreement. Suggested 1-year renewal expiry.');
      } else if (lower.includes('contract') || lower.includes('agreement') || lower.includes('nda')) {
        suggestedType = 'contract';
        suggestedCategory = 'Legal';
        suggestedTags = ['contract', 'legal', 'confidential'];
        detectedExpiry = new Date(today.getFullYear() + 2, today.getMonth(), today.getDate()).toISOString().split('T')[0];
        detectedDetails.push('Detected legal contract / non-disclosure agreement.');
      } else if (lower.includes('invoice') || lower.includes('receipt') || lower.includes('bill')) {
        suggestedType = 'receipt';
        suggestedCategory = 'Financial';
        suggestedTags = ['receipt', 'tax', 'expense'];
        detectedIssue = today.toISOString().split('T')[0];
        detectedDetails.push('Detected financial invoice / expense receipt.');
      } else if (lower.includes('tax') || lower.includes('w2') || lower.includes('1099') || lower.includes('return') || lower.includes('audit')) {
        suggestedType = 'financial_document';
        suggestedCategory = 'Financial';
        suggestedTags = ['tax', 'finance', 'compliance'];
        detectedDetails.push('Detected fiscal tax filing document.');
      } else if (lower.includes('passport') || lower.includes('visa') || lower.includes('immigration')) {
        suggestedType = 'identity_document';
        suggestedCategory = 'Personal';
        suggestedTags = ['passport', 'travel', 'official'];
        detectedExpiry = new Date(today.getFullYear() + 10, today.getMonth(), today.getDate()).toISOString().split('T')[0];
        detectedDetails.push('Detected travel / passport identity record.');
      } else if (lower.includes('license') || lower.includes('driver') || lower.includes('dl')) {
        suggestedType = 'identity_document';
        suggestedCategory = 'Personal';
        suggestedTags = ['driving-license', 'dmv', 'identity'];
        detectedExpiry = new Date(today.getFullYear() + 5, today.getMonth(), today.getDate()).toISOString().split('T')[0];
        detectedDetails.push('Detected government driving license.');
      } else if (lower.includes('cert') || lower.includes('degree') || lower.includes('diploma')) {
        suggestedType = 'certificate';
        suggestedCategory = 'Education';
        suggestedTags = ['certificate', 'academic', 'verified'];
        detectedDetails.push('Detected professional or educational accreditation certificate.');
      } else if (lower.includes('insurance') || lower.includes('policy') || lower.includes('claim')) {
        suggestedType = 'insurance_document';
        suggestedCategory = 'Insurance';
        suggestedTags = ['insurance', 'policy', 'protection'];
        detectedExpiry = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate()).toISOString().split('T')[0];
        detectedDetails.push('Detected policy documentation. Suggested annual renewal expiry.');
      } else if (lower.includes('car') || lower.includes('vehicle') || lower.includes('registration') || lower.includes('title')) {
        suggestedType = 'vehicle_document';
        suggestedCategory = 'Vehicle';
        suggestedTags = ['vehicle', 'auto', 'registration'];
        detectedDetails.push('Detected vehicle registration / title credential.');
      } else if (lower.endsWith('.pdf')) {
        suggestedType = 'pdf';
        suggestedCategory = 'Personal';
        suggestedTags = ['pdf', 'document'];
        detectedDetails.push('Standard formatted PDF document.');
      } else {
        suggestedTags = ['upload', 'verified'];
        detectedDetails.push(`Analyzed format: ${selectedFile.type || 'file'} (${formatBytes(selectedFile.size)})`);
      }

      // Populate form
      setDocumentName(formattedTitle);
      setDocumentType(suggestedType);
      setCategory(suggestedCategory);
      setTags(suggestedTags);
      if (detectedExpiry) setExpiryDate(detectedExpiry);
      if (detectedIssue) setIssueDate(detectedIssue);

      setAutoDetectInsights({
        type: suggestedType,
        category: suggestedCategory,
        tags: suggestedTags,
        expiry: detectedExpiry,
        details: detectedDetails,
      });

      setIsAnalyzing(false);
    }, 450);
  };

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    processSelectedFile(selected);
  };

  const processSelectedFile = (selected) => {
    setFile(selected);
    setFileName(selected.name);
    setFileSize(selected.size);
    setFileType(selected.type || 'application/octet-stream');

    const reader = new FileReader();
    reader.onload = () => {
      setFileDataUrl(reader.result);
    };
    reader.readAsDataURL(selected);

    analyzeFileAndSuggestMetadata(selected);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      processSelectedFile(droppedFile);
    }
  };

  const handleAddTag = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const val = tagInput.trim().replace(/^#/, '').toLowerCase();
      if (val && !tags.includes(val)) {
        setTags([...tags, val]);
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tToRemove) => {
    setTags(tags.filter((t) => t !== tToRemove));
  };

  const applyExpiryPreset = (years) => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + years);
    setExpiryDate(d.toISOString().split('T')[0]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!documentName.trim()) return;

    const payload = {
      title: documentName.trim(),
      fileName: fileName || `${documentName}.pdf`,
      fileSize: fileSize || 102400,
      fileType: fileType || 'application/pdf',
      fileDataUrl: fileDataUrl || '',
      documentType,
      category,
      description,
      issueDate,
      expiryDate,
      owner,
      tags,
      isLocked,
      lockPin: isLocked ? lockPin : '',
    };

    onSave(payload);
  };

  const fileFormatInfo = getFileFormatInfo(fileName || documentName);

  return (
    <div className="card-drawer-wrapper" role="presentation">
      <div className="card-drawer-overlay animate-fade-in" onClick={onClose} aria-hidden="true" />
      <aside
        className="card-drawer doc-drawer ios-doc-drawer document-upload-drawer animate-slide-left"
        role="dialog"
        aria-modal="true"
        aria-label={existingDocument ? 'Edit Document Details' : 'Upload Secure Document'}
      >
        {/* Modal Header */}
        <div className="card-drawer-header modal-header">
          <div className="modal-header-info">
            <div
              className="modal-icon-badge"
              style={{ background: 'rgba(6, 182, 212, 0.15)', color: '#06b6d4' }}
            >
              <UploadCloud size={20} />
            </div>
            <div className="modal-header-titles">
              <h2 className="modal-title">
                {existingDocument ? 'Edit Document Details' : 'Upload Secure Document'}
              </h2>
              <p className="modal-subtitle">
                Zero-knowledge encrypted storage. Supports PDF, Office, Images, Text & Archives.
              </p>
            </div>
          </div>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Modal Form */}
        <form className="card-drawer-form-wrapper document-upload-form" onSubmit={handleSubmit}>
          <div className="card-drawer-body modal-body document-upload-body">
            {/* File Dropzone / Picker */}
            <div
              className={`document-dropzone ${isDragOver ? 'drag-over' : ''} ${file ? 'has-file' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: 'none' }}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.webp,.txt,.csv,.zip,.rar"
                onChange={handleFileChange}
              />

              {isAnalyzing ? (
                <div className="dropzone-analyzing">
                  <Sparkles size={32} className="animate-spin text-accent" />
                  <h4>Auto-Detecting & Analyzing Document...</h4>
                  <p>Extracting metadata, document type, dates and tags with OCR heuristics</p>
                </div>
              ) : file || fileName ? (
                <div className="dropzone-file-selected">
                  <div
                    className="selected-file-icon"
                    style={{ background: `${fileFormatInfo.color}20`, color: fileFormatInfo.color }}
                  >
                    <fileFormatInfo.icon size={22} />
                  </div>
                  <div className="selected-file-meta">
                    <span className="selected-file-name">{fileName || 'Document File'}</span>
                    <span className="selected-file-size">
                      {formatBytes(fileSize)} • {fileFormatInfo.format} • Ready to Encrypt
                    </span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary change-file-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      fileInputRef.current?.click();
                    }}
                  >
                    Change File
                  </button>
                </div>
              ) : (
                <div className="dropzone-placeholder">
                  <div className="dropzone-icon-circle">
                    <UploadCloud size={24} />
                  </div>
                  <h4>Drag and drop your document here</h4>
                  <p>Or click to browse files (PDF, DOCX, XLSX, Images, ZIP up to 50MB)</p>
                  <span className="dropzone-badge">
                    <Sparkles size={12} /> Auto-Detect & Categorize Enabled
                  </span>
                </div>
              )}
            </div>

            {/* Auto Detect Insights Banner */}
            {autoDetectInsights && (
              <div className="auto-detect-insights-banner animate-fade-in">
                <div className="insights-banner-header">
                  <div className="insights-badge">
                    <Sparkles size={14} />
                    <span>Auto-Detect & Categorize Insights</span>
                  </div>
                  <span className="insights-verified-tag">
                    <CheckCircle2 size={13} /> High Confidence
                  </span>
                </div>
                <div className="insights-banner-body">
                  <p className="insights-note">
                    CipherVault suggested Document Type (<strong>{autoDetectInsights.type}</strong>),
                    Category (<strong>{autoDetectInsights.category}</strong>), and {autoDetectInsights.tags.length} tags.
                    Review or modify any fields below before saving.
                  </p>
                  <div className="insights-tags-preview">
                    {autoDetectInsights.tags.map((t) => (
                      <span key={t} className="insight-tag-pill">#{t}</span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Document Metadata Fields */}
            <div className="document-form-section">
              <h4 className="form-section-title">Document Information</h4>

              <div className="form-group">
                <label className="form-label">Document Title / Name *</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. Master Service Agreement 2026"
                  value={documentName}
                  onChange={(e) => setDocumentName(e.target.value)}
                  required
                />
              </div>

              <div className="grid-2-col" style={{ marginTop: '12px' }}>
                <div className="form-group">
                  <label className="form-label">Document Classification Type</label>
                  <select
                    className="form-input"
                    value={documentType}
                    onChange={(e) => setDocumentType(e.target.value)}
                  >
                    {PREDEFINED_DOC_TYPES.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select
                    className="form-input"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    {categories.map((c) => (
                      <option key={typeof c === 'string' ? c : c.name} value={typeof c === 'string' ? c : c.name}>
                        {typeof c === 'string' ? c : c.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group" style={{ marginTop: '12px' }}>
                <label className="form-label">Owner / Signee / Custodian</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. Nikhil Sharma or Legal Dept"
                  value={owner}
                  onChange={(e) => setOwner(e.target.value)}
                />
              </div>

              <div className="form-group" style={{ marginTop: '12px' }}>
                <label className="form-label">Description / Summary / Confidential Notes</label>
                <textarea
                  className="form-input identity-notes-textarea"
                  rows={3}
                  placeholder="Add notes, key contract clauses, renewal requirements, or policy numbers..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
            </div>

            {/* Document Dates & Expiry Tracking */}
            <div className="document-form-section">
              <div className="section-header-flex">
                <h4 className="form-section-title">Validity & Expiry Tracking</h4>
                <div className="expiry-presets">
                  <span className="preset-label">Quick Expiry:</span>
                  <button type="button" className="btn-preset-chip" onClick={() => applyExpiryPreset(1)}>+1 Year</button>
                  <button type="button" className="btn-preset-chip" onClick={() => applyExpiryPreset(2)}>+2 Years</button>
                  <button type="button" className="btn-preset-chip" onClick={() => applyExpiryPreset(5)}>+5 Years</button>
                  <button type="button" className="btn-preset-chip" onClick={() => setExpiryDate('')}>No Expiry</button>
                </div>
              </div>

              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Issue / Effective Date</label>
                  <input
                    type="date"
                    className="form-input doc-date-input"
                    value={issueDate}
                    onChange={(e) => setIssueDate(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Expiry / Renewal Date</label>
                  <input
                    type="date"
                    className="form-input doc-date-input"
                    value={expiryDate}
                    onChange={(e) => setExpiryDate(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Tags & Security */}
            <div className="document-form-section">
              <h4 className="form-section-title">Tags & Protection</h4>

              <div className="form-group">
                <label className="form-label">Tags (Press Enter to add)</label>
                <div className="tags-input-wrapper">
                  {tags.map((t) => (
                    <span key={t} className="tag-chip">
                      <span>#{t}</span>
                      <button
                        type="button"
                        className="tag-remove-btn"
                        onClick={() => handleRemoveTag(t)}
                        title={`Remove #${t}`}
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    className="tags-bare-input"
                    placeholder={tags.length === 0 ? 'Type tag and hit Enter...' : 'Add another tag...'}
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleAddTag}
                  />
                </div>
              </div>

              {/* Extra Security Lock */}
              <div className="security-lock-toggle-box">
                <label className="checkbox-toggle-label">
                  <input
                    type="checkbox"
                    checked={isLocked}
                    onChange={(e) => setIsLocked(e.target.checked)}
                    className="doc-checkbox-input"
                  />
                  <div className="toggle-text-container">
                    <span className="toggle-title">Require Authentication to View / Download</span>
                    <span className="toggle-sub">
                      Prompts for a Security PIN before decrypting or exposing document contents.
                    </span>
                  </div>
                </label>

                {isLocked && (
                  <div className="lock-pin-input-group animate-fade-in" style={{ marginTop: '12px' }}>
                    <label className="form-label">Set 4-Digit Security PIN</label>
                    <input
                      type="password"
                      maxLength={6}
                      className="form-input doc-pin-input"
                      placeholder="••••"
                      value={lockPin}
                      onChange={(e) => setLockPin(e.target.value)}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="card-drawer-footer modal-footer document-upload-footer">
            <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={!documentName.trim()}
            >
              <Lock size={14} />
              <span>{existingDocument ? 'Save Changes' : 'Encrypt & Upload Document'}</span>
            </button>
          </div>
        </form>
      </aside>
    </div>
  );
}
